import type { ReactNode } from "react";
import { Container, MarketingPage, MButton, Eyebrow } from "./ui";
import { SITE, breadcrumb } from "./seo";

/* ─── Shared document primitives ──────────────────────────────────────────── */
const UPDATED = "June 28, 2026";

function LegalLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-warm-bg font-sans">
      <Container className="pt-16 pb-24 sm:pt-20">
        <div className="mx-auto max-w-3xl">
          <header className="border-b border-warm-border pb-8">
            <Eyebrow>Legal</Eyebrow>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
              {title}
            </h1>
            <p className="mt-4 text-warm-muted">Last updated: {updated}</p>
          </header>
          <div className="mt-8">{children}</div>
        </div>
      </Container>
    </section>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="mt-10 mb-3 text-xl font-semibold text-warm-ink">{children}</h2>;
}

function P({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-[15px] leading-relaxed text-warm-muted">{children}</p>;
}

function UL({ children }: { children: ReactNode }) {
  return <ul className="mb-4 list-disc space-y-2 pl-5">{children}</ul>;
}

function LI({ children }: { children: ReactNode }) {
  return <li className="text-[15px] leading-relaxed text-warm-muted">{children}</li>;
}

function MailLink({ address }: { address: string }) {
  return (
    <a href={`mailto:${address}`} className="text-warm-accent hover:underline">
      {address}
    </a>
  );
}

function InlineLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a href={to} className="text-warm-accent hover:underline">
      {children}
    </a>
  );
}

/* ─── Privacy Policy ──────────────────────────────────────────────────────── */
export function Privacy() {
  return (
    <MarketingPage
      seo={{
        title: "Privacy Policy — WonderfulDoctor",
        description:
          "How WonderfulDoctor collects, uses, and protects clinic account data and patient PHI across AI video consultations, clinical reports, recordings, visit summaries, and billing.",
        path: "/privacy",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Privacy", path: "/privacy" },
        ]),
      }}
    >
      <LegalLayout title="Privacy Policy" updated={UPDATED}>
        <P>
          This Privacy Policy explains how {SITE.name}, a Wonderful Agent product available at{" "}
          <InlineLink to={SITE.url}>{SITE.url}</InlineLink>, collects, uses, and protects
          information — including protected health information (PHI) — when you use our platform.
          It is provided for transparency and is a template you should adapt with your own
          counsel; for contractual data-processing terms and a Business Associate Agreement,
          please contact us.
        </P>

        <H2>1. Overview &amp; scope</H2>
        <P>
          {SITE.name} is a pay-as-you-go AI telehealth platform. A clinic configures an AI video
          doctor, patients self-schedule by email, upload their clinical reports, and complete a
          recorded AI video consultation that reasons over their health history and reacts to live
          vitals; a licensed clinician reviews and signs off where required, and the patient
          receives an emailed visit summary. This policy covers both the clinics and care teams
          who use the platform and the patients who are seen through it.
        </P>
        <P>
          For patient PHI, the clinic is the covered entity and data controller, and {SITE.name}
          acts as a business associate and processor on the clinic&apos;s behalf. We act as a
          controller for the clinic account, billing, and security data we collect to operate the
          service.
        </P>

        <H2>2. Information we collect</H2>
        <UL>
          <LI>
            <strong className="text-warm-ink">Clinic account data.</strong> Names, work email
            addresses, clinic details, team roles, and authentication information for the
            clinicians and staff who administer an account.
          </LI>
          <LI>
            <strong className="text-warm-ink">Patient PHI.</strong> Minimal patient demographics;
            clinical reports (labs, imaging, DICOM, prior notes) and the findings extracted from
            them; consultation recordings and transcripts; visit summaries and SOAP notes; the
            longitudinal health journal; vitals timeseries; and consent records.
          </LI>
          <LI>
            <strong className="text-warm-ink">Usage &amp; billing data.</strong> Product usage,
            log and diagnostic data, the number of completed consultations, and the payment
            information needed to bill for paid consultations (processed by our payments provider).
          </LI>
          <LI>
            <strong className="text-warm-ink">Cookies &amp; local storage.</strong> We use only
            essential cookies and authentication tokens required to keep you signed in and to
            operate the service. We do not use third-party advertising or cross-site tracking.
          </LI>
        </UL>

        <H2>3. How we use information</H2>
        <P>We use the information we collect to:</P>
        <UL>
          <LI>Provide, maintain, and improve the telehealth platform.</LI>
          <LI>Run consultations and send the related transactional emails, including visit summaries.</LI>
          <LI>Brief the AI doctor and generate visit summaries, triage and next steps from each session.</LI>
          <LI>Maintain the health journal to support continuity of care across visits.</LI>
          <LI>Bill for completed consultations and process payments.</LI>
          <LI>Protect the security and integrity of the service and prevent abuse.</LI>
          <LI>Provide customer support and respond to requests.</LI>
          <LI>Comply with applicable legal obligations.</LI>
        </UL>

        <H2>4. AI processing &amp; clinician oversight</H2>
        <P>
          Clinical reports, the health journal, and consultation transcripts are analyzed by AI
          models to brief the AI doctor and produce a visit summary, triage and suggested next
          steps. Imaging and reports yield observations for clinician review — never an autonomous
          diagnosis. Every statement is grounded in the report or finding it is based on.
        </P>
        <P>
          AI output is decision-support and is not a definitive diagnosis or prescription. Where
          your clinical model requires it, a licensed clinician reviews and signs the visit before
          it reaches the patient; the clinic retains responsibility for clinical decisions and can
          review the evidence behind any summary.
        </P>

        <H2>5. Recordings &amp; consent</H2>
        <P>
          Consultations are recorded with the patient&apos;s consent at the start of the session.
          We capture and version consent for telehealth, recording, and data processing.
          Recordings are stored privately and served only through short-lived signed links. The
          clinic controls the retention of recordings and records associated with its account.
        </P>

        <H2>6. Sub-processors &amp; BAAs</H2>
        <P>We rely on a small set of trusted providers to operate the service:</P>
        <UL>
          <LI>
            <strong className="text-warm-ink">Tavus</strong> — AI video (avatar) provider that
            conducts and records the consultation.
          </LI>
          <LI>
            <strong className="text-warm-ink">RouterBench</strong> — AI model gateway used to run
            Claude models for reasoning, vision and embeddings.
          </LI>
          <LI>
            <strong className="text-warm-ink">Resend</strong> — transactional email delivery.
          </LI>
          <LI>
            <strong className="text-warm-ink">Stripe</strong> — payment processing.
          </LI>
          <LI>
            <strong className="text-warm-ink">Cloudflare</strong> — cloud hosting and storage
            (Workers, Pages, and R2).
          </LI>
          <LI>
            <strong className="text-warm-ink">Neon</strong> — managed Postgres database.
          </LI>
        </UL>
        <P>
          We sign Business Associate Agreements (BAAs) with sub-processors that touch PHI, and each
          provider is bound by its own data-protection commitments. Data may be processed in the
          regions in which these providers operate.
        </P>

        <H2>7. Data sharing</H2>
        <P>
          We do not sell personal data and we do not use PHI for advertising. We share information
          only with the sub-processors listed above to deliver the service, and where required to
          comply with the law or to protect our rights, users, or the public.
        </P>

        <H2>8. Security</H2>
        <P>
          We protect data with encryption in transit and at rest, role-based access controls,
          strict per-patient access scoping, signed webhooks, and audit logging of access to PHI.
          You can read more on our <InlineLink to="/security">security page</InlineLink>. Security
          concerns can be reported to <MailLink address="security@wonderfulagent.ai" />.
        </P>

        <H2>9. Data retention</H2>
        <P>
          We retain information for as long as needed to provide the service, to support continuity
          of care, and to meet our legal and medical-records retention obligations. A clinic may
          request deletion of patient records associated with its account, and we will action such
          requests subject to any legal retention requirements.
        </P>

        <H2>10. Patient &amp; data rights</H2>
        <P>
          Patients have the right to access and obtain a copy of their health information (the
          HIPAA right of access), and, depending on location, to export, amend, correct, or
          request deletion of their data (for example under GDPR). We support export in portable
          formats such as FHIR or JSON. To exercise these rights, contact your clinic, which holds
          your records as the covered entity, or reach us at{" "}
          <MailLink address="privacy@wonderfulagent.ai" /> and we will help route the request.
        </P>

        <H2>11. International transfers</H2>
        <P>
          Where data is transferred across borders, we and our providers use appropriate
          safeguards, such as standard contractual clauses, to protect it in line with
          applicable law.
        </P>

        <H2>12. Children</H2>
        <P>
          Where a clinic offers pediatric care, the consultation and PHI of a minor are handled
          under the clinic&apos;s direction and applicable law, including consent provided by a
          parent or legal guardian. We do not collect personal data directly from children without
          such authorization.
        </P>

        <H2>13. Changes to this policy</H2>
        <P>
          We may update this policy as the service evolves or as legal requirements change. When
          we make material changes, we will update the &ldquo;Last updated&rdquo; date above and,
          where appropriate, notify account administrators.
        </P>

        <H2>14. Contact</H2>
        <P>
          Questions about this policy or your data can be sent to{" "}
          <MailLink address="privacy@wonderfulagent.ai" />.
        </P>

        <div className="mt-10 border-t border-warm-border pt-8">
          <MButton to="/terms" variant="outline">
            Read our Terms of Service
          </MButton>
        </div>
      </LegalLayout>
    </MarketingPage>
  );
}

/* ─── Terms of Service ────────────────────────────────────────────────────── */
export function Terms() {
  return (
    <MarketingPage
      seo={{
        title: "Terms of Service — WonderfulDoctor",
        description:
          "The terms that govern use of WonderfulDoctor, a pay-as-you-go AI telehealth platform: the medical disclaimer, accounts, acceptable use, billing, patient consent, and liability.",
        path: "/terms",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Terms", path: "/terms" },
        ]),
      }}
    >
      <LegalLayout title="Terms of Service" updated={UPDATED}>
        <H2>1. Agreement to terms</H2>
        <P>
          These Terms of Service govern your access to and use of {SITE.name}, a Wonderful Agent
          product available at <InlineLink to={SITE.url}>{SITE.url}</InlineLink>. By creating an
          account or using the service, you agree to these terms on behalf of yourself and any
          organization you represent. They are a template you should adapt with your own counsel.
          If you do not agree, do not use the service.
        </P>

        <H2>2. The service</H2>
        <P>
          {SITE.name} is a pay-as-you-go AI telehealth platform. A clinic configures an AI video
          doctor, patients self-schedule by email, upload clinical reports, and complete a recorded
          AI video consultation that reasons over their health history and live vitals. The
          platform produces a visit summary with triage and next steps, and routes the visit to a
          licensed clinician for review and sign-off where required.
        </P>

        <H2>3. Medical disclaimer</H2>
        <P>
          {SITE.name} provides health information and triage support. It is{" "}
          <strong className="text-warm-ink">not a substitute for professional medical judgment</strong>{" "}
          and does not create a doctor–patient relationship with Wonderful Agent. The AI doctor
          does not provide a definitive diagnosis or prescription; where required, a licensed
          clinician reviews and signs off on the visit. In v1 the service is informational with a
          human in the loop where required. Always seek the advice of a qualified clinician with
          any questions about a medical condition.
        </P>
        <P>
          <strong className="text-warm-ink">Not for emergencies.</strong> Do not use {SITE.name} for
          medical emergencies. If you think you may have a medical emergency, call your local
          emergency number or 911, or go to the nearest emergency department immediately.
        </P>

        <H2>4. Accounts &amp; teams</H2>
        <UL>
          <LI>You must provide accurate account information and keep it up to date.</LI>
          <LI>
            Access is role-based; you are responsible for the actions of teammates you invite and
            for the roles you assign them.
          </LI>
          <LI>
            You are responsible for keeping your credentials secure and for all activity under
            your account.
          </LI>
        </UL>

        <H2>5. Acceptable use</H2>
        <P>You agree not to use the service to:</P>
        <UL>
          <LI>Violate any law or practice medicine outside your license or scope of practice.</LI>
          <LI>Process health data in breach of applicable privacy or healthcare laws.</LI>
          <LI>Misuse the AI, including attempts to manipulate, mislead, or distort clinical output.</LI>
          <LI>Rely on the service for emergency care or as a sole basis for clinical decisions.</LI>
          <LI>
            Probe, scan, or breach the security of the service, or scrape or extract data without
            authorization.
          </LI>
        </UL>

        <H2>6. Clinic responsibilities &amp; patient consent</H2>
        <P>
          As the clinic, you are responsible for holding the appropriate clinical licenses,
          operating within your scope of practice, and providing licensed-clinician oversight and
          sign-off where required. You are responsible for obtaining each patient&apos;s consent to
          telehealth, recording, and data processing, and for complying with the telehealth,
          recording, and AI-in-healthcare laws that apply in your jurisdiction.
        </P>
        <P>
          AI output is decision-support and is not a substitute for clinical judgment. You must not
          base final clinical decisions solely on automated output, and a licensed clinician must
          review and sign visits where the law or your clinical model requires it.
        </P>

        <H2>7. Billing</H2>
        <UL>
          <LI>The service is pay-as-you-go and billed per completed consultation.</LI>
          <LI>
            The price is set by the consult length and locked when the patient books, so the cost
            is known in advance. A valid card on file is required.
          </LI>
          <LI>Completed consultations are non-refundable.</LI>
          <LI>No-shows and incomplete sessions that do not complete are not billed.</LI>
          <LI>We do not bill insurance in v1; the clinic is responsible for any patient charges and claims.</LI>
          <LI>Prices are exclusive of taxes, which may be added where required.</LI>
          <LI>Failed or overdue payments may result in suspension of the service.</LI>
        </UL>

        <H2>8. Intellectual property</H2>
        <P>
          We own the platform, including its software, design, and underlying technology. You
          retain ownership of your content and data, including the patient health information you
          process through the service. We grant you a limited, non-exclusive, non-transferable
          license to use the platform during your subscription, and you grant us the limited
          license needed to operate the service on your behalf.
        </P>

        <H2>9. Third-party services</H2>
        <P>
          The service relies on third-party sub-processors for AI video, AI models, email,
          payments, hosting, and database services. Their availability and performance are outside
          our direct control, and your use of the service is also subject to their applicable
          terms.
        </P>

        <H2>10. Disclaimers</H2>
        <P>
          The service is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; without
          warranties of any kind, whether express or implied. AI-generated output may contain
          errors or omissions, and we do not warrant that any summary, triage, or finding is
          accurate, complete, or error-free. A qualified clinician is responsible for reviewing
          output before acting on it.
        </P>

        <H2>11. Limitation of liability</H2>
        <P>
          To the maximum extent permitted by law, neither party will be liable for indirect,
          incidental, special, consequential, or punitive damages. Our total liability arising out
          of or relating to the service will not exceed the amounts you paid to us for the service
          in the twelve months preceding the event giving rise to the claim.
        </P>

        <H2>12. Indemnification</H2>
        <P>
          You agree to indemnify and hold harmless {SITE.name} and its affiliates from claims,
          damages, and expenses arising out of your use of the service, your content, the care you
          provide, or your breach of these terms or of applicable law, including healthcare and
          privacy laws.
        </P>

        <H2>13. Termination</H2>
        <P>
          Either party may terminate this agreement at any time. We may suspend or terminate
          access if you breach these terms or use the service in a way that creates risk or
          liability. On termination, your right to use the service ends; provisions that by their
          nature should survive — such as billing for prior use, disclaimers, and limitations of
          liability — will survive.
        </P>

        <H2>14. Changes to the service &amp; terms</H2>
        <P>
          We may modify the service and these terms over time. When we make material changes to
          these terms, we will update the &ldquo;Last updated&rdquo; date above. Your continued use
          of the service after changes take effect constitutes acceptance of the updated terms.
        </P>

        <H2>15. Governing law</H2>
        <P>
          These terms are governed by the laws of [jurisdiction to be specified], without regard
          to its conflict-of-laws rules. This is a template; consult your own counsel to set the
          governing law and venue appropriate to your clinic.
        </P>

        <H2>16. Contact</H2>
        <P>
          Questions about these terms can be sent to <MailLink address="legal@wonderfulagent.ai" />.
        </P>

        <div className="mt-10 border-t border-warm-border pt-8">
          <MButton to="/privacy" variant="outline">
            Read our Privacy Policy
          </MButton>
        </div>
      </LegalLayout>
    </MarketingPage>
  );
}
